import React, { useState, useEffect } from 'react';
import { adminApiClient } from '../lib/api';
import toast from 'react-hot-toast';
import {
  Users,
  DollarSign,
  Building,
  Calendar,
  Download,
  TrendingUp,
  PieChart,
} from 'lucide-react';

interface ReportData {
  donations: {
    total: number;
    monthly: number;
    daily: number;
    byType: { [key: string]: number };
    byStatus: { [key: string]: number };
    trends: { date: string; amount: number }[];
  };
  users: {
    total: number;
    newThisMonth: number;
    byRole: { [key: string]: number };
    byStatus: { [key: string]: number };
  };
  temples: {
    total: number;
    active: number;
    topPerformers: { name: string; donations: number }[];
  };
  poojas: {
    total: number;
    bookings: number;
    revenue: number;
  };
}

const ReportsPage: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const [templesRes, usersRes, donationsRes, poojasRes, bookingsRes] = await Promise.all([
        adminApiClient.getTemples(),
        adminApiClient.getUsers(),
        adminApiClient.getDonations(),
        adminApiClient.getPoojas(),
        adminApiClient.getPoojaBookings({ limit: 10000 }) // Fetch all for stats
      ]);

      if (templesRes.success && usersRes.success && donationsRes.success && poojasRes.success) {
        const temples = templesRes.data?.temples || [];
        const users = usersRes.data?.users || [];
        const donations = donationsRes.data?.donations || [];
        const poojas = poojasRes.data?.poojas || [];
        const bookings = bookingsRes.data?.bookings || [];

        // --- Donation Stats ---
        const totalDonations = donations.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const monthlyDonations = donations
          .filter((d: any) => new Date(d.createdAt) >= startOfMonth)
          .reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

        const dailyDonations = donations
          .filter((d: any) => new Date(d.createdAt) >= startOfDay)
          .reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

        const byType: any = {};
        const byStatus: any = {};
        donations.forEach((d: any) => {
          byType[d.type] = (byType[d.type] || 0) + 1;
          byStatus[d.status] = (byStatus[d.status] || 0) + 1;
        });

        // Simple trend: Last 7 days
        const trends = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const dateStr = d.toLocaleDateString();
          const amount = donations
            .filter((donation: any) => new Date(donation.createdAt).toDateString() === d.toDateString())
            .reduce((sum: number, donation: any) => sum + (donation.amount || 0), 0);
          trends.push({ date: dateStr, amount });
        }

        // --- User Stats ---
        const newUsers = users.filter((u: any) => new Date(u.createdAt) >= startOfMonth).length;
        const userByRole: any = {};
        const userByStatus: any = {};
        users.forEach((u: any) => {
          userByRole[u.role] = (userByRole[u.role] || 0) + 1;
          userByStatus[u.isActive ? 'Active' : 'Inactive'] = (userByStatus[u.isActive ? 'Active' : 'Inactive'] || 0) + 1;
        });

        // --- Pooja Stats ---
        const poojaRevenue = bookings
          .filter((b: any) => b.paymentStatus === 'paid')
          .reduce((sum: number, b: any) => sum + (b.amount || 0), 0);

        // --- Temple Stats ---
        // Calculate top performers based on donation count associated with temple
        const templePerformance: any = {};
        donations.forEach((d: any) => {
          if (d.temple) {
            const tId = typeof d.temple === 'string' ? d.temple : d.temple._id;
            const tName = typeof d.temple === 'string' ? 'Unknown' : d.temple.name;
            if (!templePerformance[tId]) templePerformance[tId] = { name: tName, total: 0 };
            templePerformance[tId].total += d.amount;
          }
        });
        const topTemples = Object.values(templePerformance)
          .sort((a: any, b: any) => b.total - a.total)
          .slice(0, 5) as { name: string; donations: number }[];


        setReportData({
          donations: {
            total: totalDonations,
            monthly: monthlyDonations,
            daily: dailyDonations,
            byType,
            byStatus,
            trends
          },
          users: {
            total: users.length,
            newThisMonth: newUsers,
            byRole: userByRole,
            byStatus: userByStatus
          },
          temples: {
            total: temples.length,
            active: temples.filter((t: any) => t.status === 'existing').length, // 'existing' usually means approved/active in this context
            topPerformers: topTemples
          },
          poojas: {
            total: poojas.length,
            bookings: bookings.length,
            revenue: poojaRevenue
          }
        });
      } else {
        toast.error('Failed to fetch report data');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (format: string) => {
    toast.success(`Exporting as ${format}...`);
    // Implementation logic...
  };

  if (loading) return <div className="flex justify-center items-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;
  if (!reportData) return <div className="p-8 text-center text-gray-500">No data available</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-sm text-gray-500">Platform performance insights</p>
        </div>
        <div className="flex gap-2">
          <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="px-3 py-2 border rounded-lg bg-white outline-none focus:ring-2 focus:ring-orange-500/20">
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <button onClick={() => exportReport('csv')} className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Donations', val: `$${reportData.donations.total.toLocaleString()}`, icon: DollarSign, color: 'green', sub: '+12% vs last month' },
          { label: 'Active Users', val: reportData.users.total, icon: Users, color: 'blue', sub: `${reportData.users.newThisMonth} new` },
          { label: 'Total Temples', val: reportData.temples.total, icon: Building, color: 'orange', sub: `${reportData.temples.active} active` },
          { label: 'Pooja Bookings', val: reportData.poojas.bookings, icon: Calendar, color: 'purple', sub: 'Revenue: $0' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <div className={`p-2 rounded-lg bg-${stat.color}-50 text-${stat.color}-600`}>
                <stat.icon size={20} />
              </div>
              {/* <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+4.5%</span> */}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.val}</h3>
            <p className="text-sm text-gray-500">{stat.label}</p>
            <p className="text-xs text-gray-400 mt-2">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['overview', 'donations', 'users', 'temples'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${activeTab === tab ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content (Mocked Visuals for now) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><TrendingUp size={18} /> Revenue Trends</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400">
            Chart Component Visualization
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><PieChart size={18} /> Donation Distribution</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400">
            Pie Chart Visualization
          </div>
        </div>
      </div>

    </div>
  );
};

export default ReportsPage;
